import React, { useState, useEffect } from "react";
import logo from "../../images/logo.svg";
import { NavLink, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useUser } from "./UserContext";
import "./NavBar.css";
import NavDropDown from "./NavDropDown";
import LogInRegisterModal from "../landing/AuthModal";
import RegisterModal from "../landing/RegisterModal";

// Inline so it inherits `currentColor` — the hamburger then matches the navy logo
// and the middle nav links (the button sets color: var(--color-primary)).
const HamburgerIcon = () => (
  <svg
    className="img-2"
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

function NavBar() {
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();
  const { libraryEnabled, lendingTrackers, features, mobileNavOpen, setMobileNavOpen } = useUser();
  const hasLending = lendingTrackers.length > 0;
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const openRegisterModal = () => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };
  const openLoginModal = () => {
    setShowLoginModal(true);
    setShowRegisterModal(false);
  };
  const closeModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
  };

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => setLoggedIn(!!user));

    const handleResize = () => setIsSmallScreen(window.innerWidth < 1000);
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      unsub();
    };
  }, []);

  const handleLogoClick = () => {
    // The logo goes to the Sign-Out screen — the daily driver teachers live in —
    // not the Dashboard. Classroom Mode renders ClassroomNavBar instead, so this
    // never exposes it there.
    loggedIn ? navigate("/classsignoutpage") : navigate("/");
  };

  return (
    <div className="navbar-container">
      {isSmallScreen ? (
        <div className="div">
          <div className="div-2">
            <img
              loading="lazy"
              src={logo}
              className="img"
              alt="Logo"
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="div-menu">
            {loggedIn ? (
              // Logged in: the hamburger opens the full app sidebar as a drawer.
              <button
                type="button"
                className="nav-hamburger"
                aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen((open) => !open)}
              >
                <HamburgerIcon />
              </button>
            ) : (
              // Logged out: the simple public menu (Home / About / Sign Up / In).
              <NavDropDown />
            )}
          </div>
        </div>
      ) : (
        <div className="div">
          <div className="div-2">
            <img
              loading="lazy"
              src={logo}
              className="img"
              alt="Logo"
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="div-3">
            {loggedIn ? (
              <>
                <div className="div-4">
                  {features.attendance && (
                    <NavLink
                      to="/attendance"
                      className={({ isActive }) =>
                        `div-5 nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      Attendance
                    </NavLink>
                  )}
                  {features.signOut && (
                    <NavLink
                      to="/classsignoutpage"
                      className={({ isActive }) =>
                        `div-6 nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      Sign Out
                    </NavLink>
                  )}
                  {hasLending && (
                    <NavLink
                      to="/lending"
                      className={({ isActive }) =>
                        `div-6 nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      Lending
                    </NavLink>
                  )}
                  {libraryEnabled && (
                    <NavLink
                      to="/library"
                      className={({ isActive }) =>
                        `div-6 nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      Library
                    </NavLink>
                  )}
                  {features.checklist && (
                    <NavLink
                      to="/emergency"
                      className={({ isActive }) =>
                        `div-7 nav-link ${isActive ? "active" : ""}`
                      }
                    >
                      Check List
                    </NavLink>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="div-4">
                  <NavLink
                    to="/about"
                    className={({ isActive }) =>
                      `div-5 nav-link ${isActive ? "active" : ""}`
                    }
                  >
                    About
                  </NavLink>
                </div>
              </>
            )}
          </div>
          {loggedIn ? (
            // Desktop keeps the middle daily links; the Log Out button is replaced
            // by the hamburger (Log Out now lives inside the drawer, like mobile).
            <div className="div-8-logged-in">
              <button
                type="button"
                className="nav-hamburger"
                aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen((open) => !open)}
              >
                <HamburgerIcon />
              </button>
            </div>
          ) : (
            <>
              <div className="div-8">
                <div className="div-9">
                  <div className="div-10" onClick={openRegisterModal}>
                    Sign Up
                  </div>
                  <RegisterModal show={showRegisterModal} onHide={closeModals} />
                  <div className="div-11" onClick={openLoginModal}>
                    Sign In
                  </div>
                  <LogInRegisterModal show={showLoginModal} onHide={closeModals} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default NavBar;
